import { useState, type ReactNode } from 'react'
import {
  EMPTY_CELL,
  formatBookingDate,
  formatBookingMio,
  type TokenBookingRow,
} from '../utils/tokenBookings'

// TQ #960: gemeinsame Tabelle fuer Dashboard-Karte (Drilldown) und Seite Token-Verlauf.
// Spalten: Datum | Typ | Taetigkeit | [Projekt] | [Beleg] | Menge.

/** Taetigkeit: einzeilig mit Ellipsis; voller Text per title (Maus) und, fuer Tastatur/Touch,
 *  per EINEM Ausloeser aufgeklappt: Klick/Tippen oder Enter/Leertaste toggelt (Fokus allein oeffnet
 *  nicht -- sonst klappt ein Klick, der erst fokussiert und dann toggelt, sofort wieder zu).
 *  Escape und Verlassen des Fokus schliessen. tabIndex=0, aria-label mit vollem Text. */
export function TaetigkeitCell({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  if (!text) return <span style={{ color: '#9ca3af' }}>{EMPTY_CELL}</span>
  return (
    <span
      tabIndex={0}
      role="button"
      title={text}
      aria-label={`Tätigkeit: ${text}`}
      aria-expanded={open}
      data-testid="taetigkeit"
      onBlur={() => setOpen(false)}
      onClick={() => setOpen((o) => !o)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o) }
      }}
      style={{
        display: 'block',
        maxWidth: 260,
        overflow: open ? 'visible' : 'hidden',
        textOverflow: open ? 'clip' : 'ellipsis',
        whiteSpace: open ? 'normal' : 'nowrap',
        cursor: 'pointer',
      }}
    >
      {text}
    </span>
  )
}

interface Props {
  rows: TokenBookingRow[]
  showProjekt?: boolean
  showBeleg?: boolean
  renderProjekt?: (projekt: string) => ReactNode
  compact?: boolean
  footer?: { label: string; value: string }
}

export default function TokenBookingsTable({ rows, showProjekt, showBeleg, renderProjekt, compact, footer }: Props) {
  const pad = compact ? '4px 8px' : '0.75rem 0.5rem'
  const th = { padding: pad, textAlign: 'left' as const, fontWeight: 600 }
  const colCount = 4 + (showProjekt ? 1 : 0) + (showBeleg ? 1 : 0)
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 13 : '0.875rem' }}>
      <thead>
        <tr style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
          <th style={th}>Datum</th>
          <th style={th}>Typ</th>
          <th style={th}>Tätigkeit</th>
          {showProjekt && <th style={th}>Projekt</th>}
          {showBeleg && <th style={th}>Beleg</th>}
          <th style={{ ...th, textAlign: 'right' }}>Menge</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} style={{ borderTop: '1px solid #f3f4f6' }}>
            <td style={{ padding: pad, whiteSpace: 'nowrap' }}>{formatBookingDate(r.datum)}</td>
            <td style={{ padding: pad }}>{r.typ}</td>
            <td style={{ padding: pad, maxWidth: 260 }}><TaetigkeitCell text={r.taetigkeit} /></td>
            {showProjekt && (
              <td style={{ padding: pad }}>
                {r.projekt ? (renderProjekt ? renderProjekt(r.projekt) : r.projekt) : EMPTY_CELL}
              </td>
            )}
            {showBeleg && <td style={{ padding: pad }}>{r.beleg || EMPTY_CELL}</td>}
            <td style={{ padding: pad, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {formatBookingMio(r.einheitenMio)}
            </td>
          </tr>
        ))}
      </tbody>
      {footer && (
        <tfoot>
          <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
            <td colSpan={colCount - 1} style={{ padding: pad }}>{footer.label}</td>
            <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>{footer.value}</td>
          </tr>
        </tfoot>
      )}
    </table>
  )
}
